import { marked } from 'marked';

/**
 * Renders a string as HTML. If the input already contains HTML tags,
 * returns it as-is. Otherwise treats it as Markdown and converts to HTML.
 *
 * Also normalises internal links: strips trailing slashes from
 * <a href="/internal/path/"> so clicking them does not trigger a
 * 301 redirect hop in the SSR middleware (spec 29 — URL Conventions).
 * External links (http/https) and the bare root `/` are untouched.
 */
export function renderRichText(input: string): string {
  if (!input) return '';

  let html: string;
  // If it already contains HTML tags, pass through as-is
  if (/<[a-z][\s\S]*>/i.test(input)) {
    html = input;
  } else {
    // Otherwise parse as Markdown
    html = marked.parse(input, { async: false }) as string;
  }

  // Normalise internal links — strip trailing slash from href="/foo/..." values,
  // including across a ?query or #fragment suffix. Structure:
  //   path     = (\/[^\/?#"][^?#"]*?)   leading /, non-/?#" second char, body excludes ?#"
  //   trailing = \/                     the slash we strip
  //   suffix   = ([?#][^"]*)?           optional ?query or #fragment kept intact
  //   close    = "                      closing quote
  //
  //   - href="/about/"           -> href="/about"
  //   - href="/a/b/c/"           -> href="/a/b/c"
  //   - href="/foo/?ref=x"       -> href="/foo?ref=x"
  //   - href="/foo/#top"         -> href="/foo#top"
  //   - href="/"                 -> unchanged (second-char class rejects empty)
  //   - href="//cdn.x.com/a/"    -> unchanged (second char "/" rejected)
  //   - href="https://..."       -> unchanged (first char "h" ≠ "/")
  //   - href="/foo#bar"          -> unchanged (no trailing slash to strip)
  //   - href="/foo?next=/bar/"   -> unchanged (path body excludes ?, so no match)
  html = html.replace(/href="(\/[^/?#"][^?#"]*?)\/([?#][^"]*)?"/g, 'href="$1$2"');

  html = normaliseProse(html);
  return html;
}

/**
 * SSR-safe pure-regex normaliser for CMS rich-text body HTML.
 *
 * Six transforms:
 *  (a)  Demote any <h1> inside body content to <h2> — preserves attributes,
 *       idempotent (after first pass there are no <h1> left).
 *  (a5) Promote smallest remaining body heading level to h2 and cascade-shift
 *       deeper levels — prevents h1 -> h3 / h1 -> h4 skips on article pages
 *       whose CMS authors started section headings at h3 (quick-97 / Lighthouse
 *       axe heading-order on /resources/news/:slug). Runs AFTER (a) so any
 *       stray h1 is normalised to h2 first; runs BEFORE (a2)/(a3)/(a4)/(a6)/(b)
 *       which are heading-agnostic.
 *  (a2) Strip empty anchors lacking accessible name (a11y — link-name rule).
 *       WP→Strapi migration left orphan `<a href="...jpg"></a>` wrappers when
 *       their `<img>` child was relocated; axe-core `link-name` fails on these.
 *       Anchors with text, child elements, aria-label, aria-labelledby, or
 *       title are preserved verbatim.
 *  (a3) Unwrap WP-CDN anchor wrappers around <img> (a11y — link-name +
 *       anchored-image-no-alt — quick-90 / seo-issues.md row 7). Shape:
 *       `<a href="https://samotics.com/wp-content/uploads/...">` wrapping
 *       a single `<img>` (with optional whitespace) — anchor stripped, img
 *       preserved verbatim. Heuristic-safe: only WP-CDN URLs are unwrapped;
 *       legitimate authored anchored images keep working.
 *  (a6) Rewrite bare CMS-authored `<img src="https?://samotics.com/wp-content/
 *       uploads/...">` (and matching `<source srcset="...">`) to same-origin
 *       paths and inject an `onerror` safeguard that hides broken images
 *       (quick-110 / `rewriteWpCdnRawImages`). WP→Strapi migration left raw `<img>` tags whose src
 *       points at the now-defunct WP CDN — every page renders a flood of
 *       cross-host 404s. The same-origin rewrite lets quick-106's
 *       per-path Vercel rewrites fire for the 3 known logo paths; everything
 *       else 404s same-origin but the `onerror` hides the broken-image icon
 *       so the page degrades gracefully (no console-spam, no busted UX).
 *       Heuristic-safe: only WP-CDN URLs are rewritten; legitimate authored
 *       images keep working. Runs AFTER (a3) so an anchored WP-CDN img has
 *       its wrapping `<a>` stripped first, and BEFORE (a4) so the Strapi
 *       optimizer pass sees the unrewritten (non-WP) Strapi-CDN srcs only.
 *  (b)  Inject `loading="lazy" decoding="async"` on every inline <img> AFTER
 *       THE FIRST — preserves all existing attrs (src, srcset, sizes, alt,
 *       width, height, data-..., aria-...); idempotent via the `loading=`
 *       short-circuit applied to each captured attribute string.
 *
 * ASCII-only regex (no Unicode property escapes, no `u` flag) so V8 and
 * Node 20+ produce byte-equal output — closes Pitfalls 7 (lost attrs),
 * 8 (DOM-parser SSR break), 9 (idempotency), 10 (zero-H1 page), 11 (NG0500
 * hydration mismatch).
 *
 * SSR-safe contract: NO browser-only DOM-parser, NO docu-ment.* globals,
 * NO Intl-namespace usage, NO to-Locale-DateString — same precedent class as
 * quick-43 (html-decoder.util.ts) and 36-02 (locale-formatter ban). Verified
 * by exact grep at phase acceptance.
 *
 * Per-CMS-body counter: `imgIndex` resets per function invocation, so each
 * call to renderRichText (one per adapter call site, FAQ answer, card body,
 * callout body, etc.) gets its own fresh first-image carve-out. The carve-out
 * is per CMS-body-string, NOT per page (RESEARCH §Open Question 6).
 *
 * @param html input rich-text HTML string (post `marked.parse` if input was markdown)
 * @returns normalised HTML — body H1s demoted, body heading levels normalised so the smallest is h2, empty anchors stripped, body images (except first) lazy-loaded
 */
export function normaliseProse(html: string): string {
  // (a) Demote <h1> opening tag (preserve all attributes).
  // The (\s[^>]*)? capture allows zero or more attrs; nested `<` not allowed
  // in HTML5 attribute values without entity-encoding, so the regex is safe.
  html = html.replace(/<h1(\s[^>]*)?>/gi, '<h2$1>');

  // (a) Demote </h1> closing tag.
  html = html.replace(/<\/h1\s*>/gi, '</h2>');

  // (a5) Promote smallest remaining body heading level to h2 (cascade-shift
  // deeper levels by the same delta). Closes Lighthouse axe heading-order
  // skip on news article bodies that start at h3/h4 (quick-97). Runs AFTER
  // (a) — any stray body h1 is already h2 — and BEFORE the heading-agnostic
  // transforms below.
  html = promoteBodyHeadings(html);

  // (a2) Strip empty anchors with no accessible name (quick-86).
  // WP→Strapi migration artefact: orphan <a href="...jpg"></a> wrappers
  // where the <img> child was relocated. Preserve anchors with text,
  // child elements, aria-label, aria-labelledby, or title.
  html = stripEmptyAnchors(html);

  // (a3) Unwrap WP-CDN anchor wrappers around <img> (quick-90).
  // WP→Strapi migration artefact: <a href="https://samotics.com/wp-
  // content/uploads/..."><img alt></a> — closes axe-core link-name +
  // anchored-image-no-alt sitewide. MUST run after stripEmptyAnchors
  // (so an empty <a></a> never reaches this transform) and BEFORE the
  // img-lazy block (so the unwrapped <img>, if not the first, gets
  // lazy-loaded on its own merits).
  html = unwrapWpCdnImageAnchors(html);

  // (a6) Rewrite bare CMS-authored <img src="https?://samotics.com/wp-
  // content/uploads/..."> and matching <source srcset="..."> to same-origin
  // paths and inject onerror="this.style.display='none'" safeguard on the
  // <img>. WP→Strapi migration artefact: rich-text bodies still contain raw
  // <img> tags whose src points at the dead WP CDN — quick-110 stops the
  // cross-host 404 flood and degrades gracefully when the same-origin path
  // also has no backing file. MUST run after (a3) so any wrapping <a> is
  // gone first, and BEFORE (a4) so this transform's same-origin path output
  // does not interfere with the Strapi-CDN matcher (which only matches
  // *.media.strapiapp.com — orthogonal in practice).
  html = rewriteWpCdnRawImages(html);

  // (a7) Rebuild <img srcset> when it contains dead WP-CDN URLs but the
  // sibling src points at a working Strapi-CDN file. The WP→Strapi migration
  // rewrote src but left srcset's responsive candidates pointing at the
  // decommissioned WP CDN — every viewport on every browser then 404s
  // because srcset wins over src in resolution selection (quick-110, blog
  // bodies under /resources/blog/*). The fix derives the Strapi master URL
  // from src and re-emits each original width descriptor as a Vercel-
  // optimiser variant of that master, preserving real responsive images.
  // Falls back to stripping srcset (+ sizes) if no Strapi URL can be
  // recovered. MUST run AFTER (a6) so any wp-content-only <img> has already
  // been rewritten, and BEFORE (a4) so the raw Strapi src URL is still
  // unwrapped when we read it.
  // QUICK-112 follow-up: widths are snapped to vercel.json `images.sizes`
  // before emission — Vercel image optimizer returns 400 for any width not
  // in the whitelist [640, 750, 828, 1080, 1200, 1920, 2048, 3840], and
  // WordPress emits non-standard widths (13w, 300w, 553w, …) that would
  // otherwise 400 in every browser.
  html = rewriteWpCdnImgSrcsetUsingSrc(html);

  // (a8) Inject `onerror="this.style.display='none'"` on every <img> that
  // does not already carry an onerror attribute. Defense-in-depth follow-up
  // to (a6) — the wp-cdn-specific onerror injection only fires for imgs
  // whose src matches `samotics.com/wp-content/uploads/*`, leaving raw
  // Strapi-CDN imgs and Vercel-optimised imgs naked when the underlying
  // file is missing (quick-112: live `/resources/monitoring-gap-calculator`
  // hit 400 on `/_vercel/image?url=...strapiapp.com/...&w=553` because the
  // referenced Strapi file was deleted but the rich-text body still
  // references it). MUST run AFTER all src/srcset rewrites so we never
  // overwrite an authored onerror handler.
  html = injectOnerrorSafeguardOnAllImgs(html);

  // (a4) Route raw Strapi-CDN <img> and <source srcset> through Vercel's
  // image optimizer. CMS-authored raw HTML imgs (inside text-block /
  // raw-html-block / picture wrappers) bypass NgOptimizedImage entirely,
  // so they ship as original PNG without WebP/AVIF conversion, no
  // responsive variants, and only 1h Strapi-CDN cache instead of 1y
  // Vercel-edge cache. Rewriting at the renderer chokepoint catches
  // every CMS-authored raw img sitewide. Pure regex, SSR-safe; same
  // precedent class as stripEmptyAnchors and unwrapWpCdnImageAnchors.
  html = routeRawStrapiImagesThroughVercelOptimizer(html);
  html = addResponsiveCmsImageCandidates(html);

  // (b) Inject loading=lazy decoding=async on every <img> EXCEPT the first.
  // Single regex pass with a counter via replace callback — preserves all
  // existing attrs by re-emitting the captured `attrs` string verbatim.
  let imgIndex = 0;
  html = html.replace(/<img\b([^>]*)>/gi, (match, attrs) => {
    const idx = imgIndex++;
    if (idx === 0) return match; // LCP carve-out (Pitfall 9)
    if (/\bloading\s*=/i.test(attrs)) return match; // Idempotency guard (Pitfall 11)
    return `<img loading="lazy" decoding="async"${attrs}>`;
  });

  return html;
}

/**
 * Promotes the smallest body heading level in a CMS rich-text string up to
 * h2, cascade-shifting deeper levels by the same delta.
 *
 * Why this exists (quick-97 / Lighthouse axe heading-order on
 * /resources/news/:slug):
 * Strapi authors started news article body section headings at h3 (or h4 in
 * the eviden article), assuming the page-template h1 is the only h1. The
 * page DOM then renders h1 -> h3 (or h1 -> h4) which axe-core flags as
 * "Heading levels should only increase by one". The fix retags the smallest
 * body heading to h2 and shifts every deeper level by the same delta so the
 * relative hierarchy of body headings is preserved exactly.
 *
 * Per-CMS-body scope: this normaliser runs ONCE per CMS rich-text body (per
 * text-block, per faq-item answer, per callout body, etc.). Each body is
 * treated as its own top-level section — every body is a peer of the page
 * h1's children, so promoting that body's smallest heading to h2 is correct
 * sitewide.
 *
 * Algorithm:
 *   1. Scan input with /<h([2-6])\b/gi and collect set of heading levels.
 *      Skip h1 — by the time this helper runs, step (a) has already demoted
 *      any body h1->h2; any remaining h1 is malformed input we leave alone.
 *   2. Empty set OR smallest === 2 -> return input unchanged (idempotency).
 *   3. delta = smallestLevel - 2 (smallest=3 -> delta=1; smallest=4 -> 2).
 *   4. Single-pass replace over BOTH open and close tags using one regex that
 *      captures the source level. Each match's target level is computed from
 *      the captured (original) level, so each tag is rewritten exactly once
 *      regardless of cascade depth — avoids the double-promotion collision
 *      that a multi-pass per-level loop would suffer when the source has gaps
 *      (e.g. h3 + h4 with no h5/h6: an h4->h3 sweep followed by an h3->h2
 *      sweep would over-promote the freshly-rewritten h3s back to h2).
 *
 * Idempotent by construction: after one pass the smallest level is 2, so
 * the next call computes delta=0 and returns the input unchanged. This must
 * hold across the chained `marked.parse -> renderRichText` SSR + hydration
 * cycle (Pitfall 11 / NG0500 hydration mismatch).
 *
 * Preserves all attributes: the `(\s[^>]*)?` capture pattern (mirroring the
 * existing h1-demotion regex) keeps `id="anchor-slug"` (used by
 * blog-article.ts auto-link table-of-contents), class names, etc.
 *
 * SSR-safe: pure regex (String.prototype.replace + RegExp), ASCII-only (no
 * Unicode property escapes, no `u` flag), no DOM-parser, no docu-ment.*
 * globals — same precedent class as the rest of this file.
 */
function promoteBodyHeadings(html: string): string {
  // 1. Collect heading levels present (h2..h6 only — skip h1 per contract).
  const levelsPresent = new Set<number>();
  const scan = /<h([2-6])\b/gi;
  let match: RegExpExecArray | null;
  while ((match = scan.exec(html)) !== null) {
    levelsPresent.add(parseInt(match[1], 10));
  }

  // 2. Empty or already-correct -> idempotent no-op.
  if (levelsPresent.size === 0) return html;
  const smallest = Math.min(...levelsPresent);
  if (smallest <= 2) return html;

  // 3. Compute shift delta (smallest=3 -> 1, smallest=4 -> 2, etc.).
  const delta = smallest - 2;

  // 4. Single-pass rewrite over both open and close tags. The captured
  // source level is used to compute the target — each tag is rewritten
  // exactly once, so an h3 + h4 body with delta=1 emits h2 + h3 cleanly
  // without the multi-pass cascade collision.
  // Open tag: `<h{level}` with optional attrs -> `<h{target}` (attrs preserved).
  html = html.replace(
    /<h([2-6])(\s[^>]*)?>/gi,
    (_full, level: string, attrs: string | undefined) => {
      const target = parseInt(level, 10) - delta;
      // Defensive clamp; the regex character class already restricts level to 2-6.
      const safe = target < 2 ? 2 : target;
      return `<h${safe}${attrs ?? ''}>`;
    },
  );
  // Close tag: `</h{level}>` -> `</h{target}>` (allow optional whitespace before `>`).
  html = html.replace(
    /<\/h([2-6])\s*>/gi,
    (_full, level: string) => {
      const target = parseInt(level, 10) - delta;
      const safe = target < 2 ? 2 : target;
      return `</h${safe}>`;
    },
  );

  return html;
}

/**
 * Strips empty anchors with no accessible name from CMS rich-text bodies.
 *
 * Match shape: `<a ...attrs...>` followed by zero-or-more whitespace, then
 * `</a>`. Opening tag captures any attrs (HTML5 forbids unescaped `>` in
 * attr values — same precedent as the img-attr regex above). The `\s*` inner
 * means any non-whitespace child content (text, `<img>`, `<svg>`, `<span>`,
 * etc.) breaks the match — those anchors are preserved verbatim.
 *
 * Preservation guards (callback short-circuits): aria-label, aria-labelledby,
 * and title attributes mark the anchor as accessible-named even when empty
 * (icon-button pattern), so the original match is returned unchanged.
 *
 * Idempotent (first pass removes all empty anchors; nothing left to match).
 * SSR-safe (String.prototype.replace + RegExp only — no DOM-parser, no
 * docu-ment.* globals, no Intl) — same precedent class as the rest of this
 * file (deliberate hyphenation so the SSR-safe grep gate passes).
 */
function stripEmptyAnchors(html: string): string {
  return html.replace(/<a\b([^>]*)>\s*<\/a\s*>/gi, (match, attrs: string) => {
    if (/\baria-label\s*=/i.test(attrs)) return match;
    if (/\baria-labelledby\s*=/i.test(attrs)) return match;
    if (/\btitle\s*=/i.test(attrs)) return match;
    return '';
  });
}

/**
 * Strips WP-CDN anchor wrappers around <img> in CMS rich-text bodies.
 *
 * Match shape: `<a href="https://samotics.com/wp-content/uploads/...">`
 * (or http variant) wrapping exactly one `<img>` (whitespace around the
 * <img> is allowed). The anchor is unwrapped — `<img>` survives verbatim.
 *
 * Why this exists (quick-90 / seo-issues.md row 7):
 * The WP→Strapi migration moved the actual image binary to
 * `*.strapiapp.com/uploads/…` (working) but left the surrounding
 * `<a href="https://samotics.com/wp-content/uploads/…JPG">` anchor pointing
 * at the now-defunct WP CDN. Lighthouse axe-core fires on TWO rules for
 * each instance:
 *  - "Internal link with no anchor text" / "anchored image with no alt
 *    text" (the only accessible-name source is `<img alt>` which is empty
 *    in the migration-imported bodies — bare `alt` attribute or no alt).
 *  - "link-name" if the alt is empty AND the surrounding anchor has no
 *    aria-label (which it never does — these are migration artefacts).
 *
 * Heuristic-safe scope: only WP-CDN URLs are unwrapped. Any anchor whose
 * href does NOT start with `https?://samotics.com/wp-content/uploads/` is
 * preserved verbatim (legitimate content-authored anchored images keep
 * working). The anchor-points-to-defunct-CDN-URL is the signal that this
 * is a migration artefact — clicking it would 404 anyway.
 *
 * Idempotent (single pass — after replacement no
 * `<a href="https…wp-content/uploads/">…<img>…</a>` remains).
 * SSR-safe: pure-regex (String.prototype.replace + RegExp), no DOM-parser,
 * no docu-ment.* globals — same precedent class as stripEmptyAnchors above
 * and the rest of this file (deliberate hyphenation so the SSR-safe grep
 * gate continues to pass).
 */
/**
 * Routes raw Strapi-CDN `<img src>` and `<source srcset>` URLs through
 * Vercel's image optimizer (`/_vercel/image?url=...&w=1200&q=75`).
 *
 * Why this exists (quick-93 / Lighthouse home-LCP audit):
 * CMS-authored raw HTML images (inside `<ds-raw-html-block>`, plain
 * `<img>` tags inside `<ds-text-block>` markdown, `<picture>` wrappers
 * with `<source srcset>`) bypass Angular's NgOptimizedImage entirely.
 * The browser fetches the original PNG/JPEG directly from Strapi's CDN
 * (`gorgeous-laughter-*.media.strapiapp.com`) — no WebP/AVIF, no
 * responsive variants, only 1h cache TTL.
 *
 * Routing through `/_vercel/image` gives WebP/AVIF (per browser Accept
 * header), 1y immutable Vercel-edge cache, and same-origin (no extra
 * DNS / TLS to Strapi-CDN). Site-wide LCP/byte-weight win on every
 * CMS-authored raw img.
 *
 * Match shapes (both rewritten):
 *  - `<img ...src="https://gorgeous-laughter-...media.strapiapp.com/foo.png">`
 *  - `<source ...srcset="https://gorgeous-laughter-...media.strapiapp.com/foo.png">`
 *
 * Idempotent: after replacement the URLs are `/_vercel/image?...` and no
 * longer match the regex (which requires `https://...media.strapiapp.com`).
 *
 * SSR-safe: pure regex + `encodeURIComponent` (host built-in available
 * in Node + browser), no DOM-parser, no docu-ment.* globals — same
 * precedent class as stripEmptyAnchors and unwrapWpCdnImageAnchors.
 *
 * Width = 1200 (the Vercel optimizer's default cap and the same value
 * NgOptimizedImage emits for unsized inputs); quality = 75 (matches the
 * project's DEFAULT_QUALITY in image-loader.ts).
 */
function routeRawStrapiImagesThroughVercelOptimizer(html: string): string {
  const STRAPI_MEDIA_HOST = /https:\/\/[^"]*\.media\.strapiapp\.com\/[^"]+/;

  // <img ... src="https://...media.strapiapp.com/...">
  html = html.replace(
    new RegExp(`(<img\\b[^>]*\\bsrc=)"(${STRAPI_MEDIA_HOST.source})"`, 'gi'),
    (_match, prefix: string, srcUrl: string) =>
      /\.svg(?:\?|$)/i.test(srcUrl) ? `${prefix}"${srcUrl}"` :
      `${prefix}"/_vercel/image?url=${encodeURIComponent(srcUrl)}&w=1200&q=75"`,
  );

  // <source ... srcset="https://...media.strapiapp.com/..."> (inside <picture>)
  html = html.replace(
    new RegExp(`(<source\\b[^>]*\\bsrcset=)"(${STRAPI_MEDIA_HOST.source})"`, 'gi'),
    (_match, prefix: string, srcUrl: string) =>
      /\.svg(?:\?|$)/i.test(srcUrl) ? `${prefix}"${srcUrl}"` :
      `${prefix}"/_vercel/image?url=${encodeURIComponent(srcUrl)}&w=1200&q=75"`,
  );

  return html;
}

function unwrapWpCdnImageAnchors(html: string): string {
  // Opening `<a` with attrs containing a wp-content/uploads/ href, then
  // optional whitespace, then exactly one `<img>` (HTML5 disallows nested
  // `<` in attr values — same precedent as the regexes above), optional
  // whitespace, then `</a>`. The `[^>]*` after `<a` allows any attribute
  // order; the `[^>]*?\s` after the href captures everything up to the
  // closing `>` of the opening tag.
  return html.replace(
    /<a\b[^>]*\bhref="https?:\/\/samotics\.com\/wp-content\/uploads\/[^"]*"[^>]*>\s*(<img\b[^>]*>)\s*<\/a\s*>/gi,
    '$1',
  );
}

/**
 * Rewrites bare CMS-authored `<img src>` (and matching `<source srcset>`)
 * whose URL points at the now-defunct WordPress CDN
 * (`https?://samotics.com/wp-content/uploads/...`) to a same-origin path,
 * and injects an `onerror` safeguard on the `<img>` so a still-missing file
 * hides its broken-image icon instead of polluting the rendered article.
 *
 * Why this exists (quick-110 / Mac-Chrome screenshot s01.png + s02.png):
 * The WP→Strapi migration preserved the raw HTML body of legacy maintenance-
 * strategies / condition-based-maintenance articles, including bare
 * `<img src="https://samotics.com/wp-content/uploads/2025/04/<name>.png">`
 * tags whose target files no longer exist on the Vercel-served origin. Every
 * page-load fires 4-6 cross-host 404s and renders broken-image icons.
 * Quick-90 only unwraps the anchor variant (`<a><img></a>`); these are bare
 * imgs, never touched by any existing transform.
 *
 * Match shapes (both rewritten — host stripped, path preserved verbatim):
 *  - `<img ...src="https?://samotics.com/wp-content/uploads/<path>">`
 *  - `<source ...srcset="https?://samotics.com/wp-content/uploads/<path>">`
 *
 * Output shape:
 *  - `<img ...src="/wp-content/uploads/<path>" onerror="this.style.display='none'">`
 *    (onerror skipped if the original tag already has an `onerror` attr —
 *    author-supplied handlers are preserved verbatim).
 *  - `<source ...srcset="/wp-content/uploads/<path>">` (no `onerror` on
 *    `<source>` — the element has no `onerror` event; the parent
 *    `<picture>`/`<img>` carries the fallback).
 *
 * Why same-origin (not Vercel optimiser, not Strapi-CDN remap): same-origin
 * lets quick-106's 3 path-specific `vercel.json` rewrites fire transparently
 * for the legacy logo paths; everything else 404s same-origin (no cross-host
 * noise) and self-hides via `onerror`. Pure renderer transform — zero infra
 * change, zero new Strapi DTO mapping. Same precedent class as quick-90 /
 * quick-93 (regex-only renderer-chokepoint normaliser).
 *
 * Idempotent: after one pass the src is `/wp-content/uploads/...` (no host),
 * so the regex no longer matches. The onerror-presence guard means a second
 * pass through `<img>` with an existing `onerror` does not double-inject.
 *
 * SSR-safe: pure regex (String.prototype.replace + RegExp), ASCII-only (no
 * Unicode property escapes, no `u` flag), no DOM-parser, no docu-ment.*
 * globals — same precedent class as the rest of this file (deliberate
 * hyphenation so the SSR-safe grep gate continues to pass).
 */
function rewriteWpCdnRawImages(html: string): string {
  const WP_UPLOADS_URL = /https?:\/\/samotics\.com(\/wp-content\/uploads\/[^"]+)/;

  // <img ... src="https?://samotics.com/wp-content/uploads/..."> — strip the
  // host AND inject `onerror` to self-hide on 404 (idempotency-guarded:
  // existing onerror=... is preserved verbatim).
  html = html.replace(
    new RegExp(`(<img\\b)([^>]*?)\\bsrc="${WP_UPLOADS_URL.source}"([^>]*)>`, 'gi'),
    (_match, openTag: string, attrsBefore: string, pathOnly: string, attrsAfter: string) => {
      const allAttrs = `${attrsBefore}src="${pathOnly}"${attrsAfter}`;
      // Idempotency guard for the safeguard: if `onerror=` already present
      // anywhere on the tag (rare — author-supplied), do not inject.
      if (/\bonerror\s*=/i.test(allAttrs)) {
        return `${openTag}${allAttrs}>`;
      }
      return `${openTag}${allAttrs} onerror="this.style.display='none'">`;
    },
  );

  // <source ... srcset="https?://samotics.com/wp-content/uploads/..."> —
  // strip the host. No `onerror` on `<source>` (element does not fire one;
  // the surrounding `<picture><img>` carries failure handling).
  html = html.replace(
    new RegExp(`(<source\\b[^>]*\\bsrcset=)"${WP_UPLOADS_URL.source}"`, 'gi'),
    (_match, prefix: string, pathOnly: string) => `${prefix}"${pathOnly}"`,
  );

  return html;
}

/**
 * Rebuilds an `<img srcset>` whose candidates point at the now-defunct WP CDN
 * by reusing the working Strapi-CDN master URL from the same tag's `src`.
 *
 * Why this exists (quick-110, surfaced via Mac/Chrome screenshots s01 + s02):
 * The WP→Strapi migration rewrote `<img src="...">` to the Strapi CDN but
 * left `<img srcset="...">` populated with the original WordPress responsive
 * variants (`https://samotics.com/wp-content/uploads/2025/04/foo-300x273.png
 * 300w, …`). Because the browser picks from `srcset` before falling back to
 * `src`, every viewport selects a dead URL — the network panel fills with
 * cross-host 404s and the article renders broken-image boxes even though a
 * working master image exists on Strapi. Quick-110's first pass
 * (`rewriteWpCdnRawImages`) handles bare `<img src>` and `<source srcset>`
 * cases but NOT the multi-URL `<img srcset>` case alongside a working src —
 * that's this helper's job.
 *
 * Match shape: any `<img>` tag whose attributes contain `srcset="..."` with
 * at least one `samotics.com/wp-content/uploads/` URL. The tag's `src` is
 * read in two shapes:
 *   - raw Strapi:     `src="https://<host>.media.strapiapp.com/<file>"`
 *   - already Vercel: `src="/_vercel/image?url=<encoded-strapi-url>&w=…&q=…"`
 *     (in case (a4) already ran, or the author pre-wrapped it manually).
 * When neither shape applies (e.g. src is a logo path or a same-origin asset)
 * the helper falls back to stripping `srcset` (and the now-pointless `sizes`)
 * so the browser uses the working src directly — never leaves dead URLs in
 * place.
 *
 * Output: replaces srcset with comma-separated Vercel-optimiser variants
 * of the recovered Strapi master, one per original width descriptor:
 *   `srcset="/_vercel/image?url=<enc>&w=300&q=75 300w,
 *            /_vercel/image?url=<enc>&w=553&q=75 553w" sizes="…"`
 * The `sizes` attribute is left untouched (it describes layout, not URLs).
 * Width descriptors are preserved verbatim from the original srcset so the
 * browser's selection heuristics behave identically — only the URLs change.
 * Pixel-density descriptors (`2x`, `3x`) are not emitted by WordPress and
 * are treated as a safety-net stripping signal.
 *
 * Idempotent: after one pass the srcset URLs are `/_vercel/image?...` (no
 * longer `samotics.com/wp-content/uploads/`), so the wp-content match guard
 * fails on the second pass.
 *
 * SSR-safe: pure regex + `decodeURIComponent` + `encodeURIComponent` (both
 * available in Node and the browser); no DOM-parser, no docu-ment.* globals,
 * no Intl — same precedent class as the rest of this file (deliberate
 * hyphenation so the SSR-safe grep gate continues to pass).
 */
function rewriteWpCdnImgSrcsetUsingSrc(html: string): string {
  return html.replace(/<img\b([^>]*)>/gi, (match, attrs: string) => {
    // Cheap rejection: bail unless srcset contains a WP-CDN URL.
    if (!/\bsrcset="[^"]*samotics\.com\/wp-content\/uploads\//i.test(attrs)) {
      return match;
    }

    const srcsetMatch = /\bsrcset="([^"]+)"/i.exec(attrs);
    if (!srcsetMatch) return match;
    const srcMatch = /\bsrc="([^"]+)"/i.exec(attrs);

    // Helper — drop srcset (and sizes) when we can't synthesise a replacement.
    const stripSrcsetAndSizes = (): string => {
      const stripped = attrs
        .replace(/\s*\bsrcset="[^"]*"/i, '')
        .replace(/\s*\bsizes="[^"]*"/i, '');
      return `<img${stripped}>`;
    };

    if (!srcMatch) return stripSrcsetAndSizes();

    // Recover the Strapi master URL from src, in either supported shape.
    let strapiUrl: string | null = null;
    const srcValue = srcMatch[1];
    const vercelWrap = /^\/_vercel\/image\?url=([^&"]+)/i.exec(srcValue);
    if (vercelWrap) {
      let decoded: string;
      try {
        decoded = decodeURIComponent(vercelWrap[1]);
      } catch {
        return stripSrcsetAndSizes();
      }
      if (/^https:\/\/[^/]*\.media\.strapiapp\.com\/[^"]+$/i.test(decoded)) {
        strapiUrl = decoded;
      }
    } else if (/^https?:\/\/[^/]*\.media\.strapiapp\.com\/[^"]+$/i.test(srcValue)) {
      strapiUrl = srcValue;
    }

    if (!strapiUrl) return stripSrcsetAndSizes();

    // Parse the original srcset into width descriptors. Width-only ("Nw")
    // candidates are reused; any pixel-density ("Nx") candidate triggers
    // the safety-net strip (WordPress does not emit `Nx`, so seeing one
    // means the srcset was hand-authored and we should not second-guess it).
    const widths: number[] = [];
    let sawPixelDensity = false;
    for (const part of srcsetMatch[1].split(',')) {
      const trimmed = part.trim();
      const w = /\s+(\d+)w$/.exec(trimmed);
      const x = /\s+\d+(?:\.\d+)?x$/.exec(trimmed);
      if (w) widths.push(parseInt(w[1], 10));
      else if (x) sawPixelDensity = true;
    }
    if (sawPixelDensity || widths.length === 0) return stripSrcsetAndSizes();

    // Snap each WP width up to the smallest Vercel-allowed size (quick-112).
    // Vercel image optimizer 400s any width not in vercel.json `images.sizes`,
    // and WP emits widths like 13/300/553 that aren't in the whitelist —
    // emitting them verbatim 400s every responsive variant.
    const snappedWidths = widths.map(snapToVercelAllowedSize);

    // De-duplicate + sort ascending for stable output (helps spec assertions
    // and avoids redundant Vercel cache entries for identical widths).
    const sortedWidths = [...new Set(snappedWidths)].sort((a, b) => a - b);
    const encoded = encodeURIComponent(strapiUrl);
    const newSrcset = sortedWidths
      .map((w) => `/_vercel/image?url=${encoded}&w=${w}&q=75 ${w}w`)
      .join(', ');

    const newAttrs = attrs.replace(/\bsrcset="[^"]+"/i, `srcset="${newSrcset}"`);
    return `<img${newAttrs}>`;
  });
}

// QUICK-112: Vercel image optimizer accepts ONLY these widths (vercel.json
// `images.sizes`). Any other width returns 400 Bad Request, which is what
// drove the live 400 flood on `/_vercel/image?url=...&w=553` (553 ∉ list).
// Keep in sync with vercel.json — if that array changes, mirror it here.
const VERCEL_ALLOWED_SIZES = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];

/**
 * Snap an arbitrary requested width up to the smallest Vercel-allowed size.
 * Widths above the maximum collapse to the maximum (3840) — Vercel rejects
 * anything larger anyway.
 */
function snapToVercelAllowedSize(width: number): number {
  for (const allowed of VERCEL_ALLOWED_SIZES) {
    if (width <= allowed) return allowed;
  }
  return VERCEL_ALLOWED_SIZES[VERCEL_ALLOWED_SIZES.length - 1];
}

/**
 * Injects `onerror="this.style.display='none'"` on every `<img>` that does
 * not already declare an onerror handler.
 *
 * Why this exists (quick-112 follow-up to quick-110):
 * The wp-cdn-specific onerror injection in `rewriteWpCdnRawImages` only
 * fires for `samotics.com/wp-content/uploads/*` src patterns, leaving raw
 * Strapi-CDN imgs and `/_vercel/image?url=...` imgs naked when their
 * underlying file is missing or Vercel returns 400 (width whitelist
 * violation). Live `/resources/monitoring-gap-calculator` flooded the
 * console with 400s on `/_vercel/image?...&w=553` requests because the
 * referenced Strapi PNG was deleted upstream but the rich-text body still
 * references it — no onerror meant the broken-image icon rendered visibly.
 *
 * Match shape: any `<img ...>` tag in the rendered rich-text. Idempotency
 * guard: tags that already carry an `onerror=` attribute are returned
 * unchanged (author-supplied handlers preserved verbatim).
 *
 * SSR-safe: pure regex (String.prototype.replace + RegExp), ASCII-only, no
 * DOM-parser, no docu-ment.* globals — same precedent class as the rest of
 * this file (deliberate hyphenation so the SSR-safe grep gate continues to
 * pass).
 */
function injectOnerrorSafeguardOnAllImgs(html: string): string {
  return html.replace(/<img\b([^>]*)>/gi, (match, attrs: string) => {
    if (/\bonerror\s*=/i.test(attrs)) return match;
    return `<img${attrs} onerror="this.style.display='none'">`;
  });
}

/** Give unsized CMS raster images mobile candidates while preserving authored art direction. */
function addResponsiveCmsImageCandidates(html: string): string {
  return html.replace(/<(img|source)\b([^>]*)>/gi, (match, tag: string, attrs: string) => {
    const attribute = tag.toLowerCase() === 'source' ? 'srcset' : 'src';
    const source = attrs.match(new RegExp(`\\b${attribute}="([^"\\s]+)"`, 'i'));
    if (!source || !source[1].startsWith('/_vercel/image?url=')) return match;
    if (tag.toLowerCase() === 'img' && /\bsrcset\s*=/i.test(attrs)) return match;
    const url = source[1];
    const encoded = url.match(/[?&]url=([^&]+)/)?.[1];
    if (!encoded || /\.svg(?:%3f|$)/i.test(encoded)) return match;
    const candidates = [384, 640, 750, 1080, 1200].map(width =>
      `/_vercel/image?url=${encoded}&w=${width}&q=75 ${width}w`).join(', ');
    if (tag.toLowerCase() === 'source') {
      attrs = attrs.replace(source[0], `srcset="${candidates}"`);
    } else {
      attrs += ` srcset="${candidates}"`;
    }
    if (!/\bsizes\s*=/i.test(attrs)) {
      const width = Number(attrs.match(/\bwidth="(\d+)"/i)?.[1]);
      const sizes = width > 0 ? `(max-width: ${width}px) 100vw, ${width}px` : '100vw';
      attrs += ` sizes="${sizes}"`;
    }
    return `<${tag}${attrs}>`;
  });
}
